// @flow
/* eslint-disable no-underscore-dangle */
import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { loadableReady } from '@loadable/component';
import { useSSR } from 'react-i18next';
import 'i18n-client'; // Webpack alias for src/i18n client files
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/cy';
import { useTranslation } from 'react-i18next';
import getApiClient from '@justpark/api/src/apiClient';
import userTracking from '@justpark/api/src/requests/userTracking';
import trackingLongSession from '@justpark/api/src/requests/trackingLongSession';
import ApiContext from '@justpark/ui/src/components/ApiContext';
import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  ApolloProvider,
  ApolloLink,
  from
} from '@apollo/client';
import createStore from './create';
import bugsnagClient from './helpers/BugsnagClient';
import { getQueryParam, getCookieValue } from './helpers/utils';
import { client } from './api/Api';
import config from '../config/config';
import { selectLanguage, selectPartnerName } from './stores/partner';
import { gtagTrack } from './helpers/Tracking/Analytics';
import cacheConfig from './apollo/cacheConfig';
import getAppRoutes from './getAppRoutes';

declare var __CLIENT__: boolean;
declare var __LEGACY__: boolean;
declare var __DEVELOPMENT__: boolean;
// eslint-disable-next-line camelcase
declare var __webpack_modules__: Array<any>;

const locale = selectLanguage(window.__data);
const partnerName = selectPartnerName(window.__data);

client.setLocale(locale);
client.setPartner(partnerName);

const jpApiClient = getApiClient({
  baseUrl: '',
  headers: {
    'Accept-Language': locale,
    'X-JP-Partner': partnerName
  },
  errorReporter: (error) => {
    const { config: requestConfig, response } = error;

    const httpStatus = response?.status;
    const requestURL = requestConfig?.url;
    const v5ErrorCode = response?.data?.error;
    const v4ErrorCode = response?.data?.data?.code;

    const errorCode = v5ErrorCode || v4ErrorCode;

    if (
      httpStatus !== undefined &&
      requestURL !== undefined &&
      errorCode !== undefined
    ) {
      gtagTrack('api_error', {
        httpStatus,
        errorCode,
        requestURL
      });
    }
  }
});

const userTrackingRequest = userTracking({
  referrer: document.referrer,
  requestUri: window.location.href,
  source: getQueryParam(window.location.href, 'source')
});

jpApiClient(userTrackingRequest).catch(console.error);

// Store an initial session in the local storage if one doesn't exist already
// This is used for referral tracking
const jpTracking = getCookieValue('jp_session');
if (!jpTracking) {
  let affiliateId = getQueryParam(window.location.href, 'aff');
  affiliateId = affiliateId ? affiliateId.replace('aff', '') : null;
  const inviteCode = getQueryParam(window.location.href, 'invite_code');

  const sessionRequest = trackingLongSession({
    referrer: document.referrer,
    requestUri: window.location.href,
    affiliateId: Number.isNaN(affiliateId) ? null : affiliateId,
    inviteCode
  });

  jpApiClient(sessionRequest).catch(console.error);
}

const ErrorBoundary = bugsnagClient.getPlugin('react');

const ayncReducers = [
  'booking',
  'bookingPayments',
  'bookingsMade',
  'bookingsReceived',
  'bookingEarnings',
  'withdrawals',
  'dashboardAvailability',
  'seasonTicketDetails',
  'evListingsChargerDetails',
  'evSignup',
  'forms/seasonTickets',
  'listingPhotos',
  'forms/profile',
  'listingOnboarding'
];

const apiClientContext = { jpApiClient };

const clientLocationMiddleware = (store) =>
  new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        'client-location': JSON.stringify(
          store.getState().geolocation.coordinates
        )
      }
    }));

    return forward(operation);
  });

const getApolloClient = (() => {
  let apolloClient;
  return (store) => {
    // eslint-disable-next-line no-underscore-dangle
    const apolloState = window.__APOLLO_STATE__;

    if (!apolloClient) {
      apolloClient = new ApolloClient({
        link: from([
          clientLocationMiddleware(store),
          new HttpLink({
            uri: '/graphql',
            headers: {
              'Accept-Language': locale
            }
          })
        ]),
        cache: new InMemoryCache(cacheConfig).restore(apolloState)
      });
    }

    return apolloClient;
  };
})();

const clientComponent = (asyncReducers) => () => {
  useSSR(window.__i18nStore, window.__i18nLanguage);
  const { t, i18n } = useTranslation();

  const language = i18n.language === 'en' ? 'en-gb' : i18n.language;

  dayjs.locale(language);

  const store = createStore(
    client,
    window.__data,
    t,
    jpApiClient,
    asyncReducers
  );
  const apolloClient = getApolloClient(store);
  const router = createBrowserRouter(
    getAppRoutes(jpApiClient, apolloClient, store.getState())
  );

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <ApolloProvider client={apolloClient}>
          <ApiContext.Provider value={apiClientContext}>
            <RouterProvider router={router} />
          </ApiContext.Provider>
        </ApolloProvider>
      </Provider>
    </ErrorBoundary>
  );
};

loadableReady(() => {
  const importPromises = ayncReducers
    .filter((module) => {
      // https://webpack.js.org/api/module-methods/#requireresolveweak
      const resolved =
        __webpack_modules__[require.resolveWeak(`./stores/${module}`)];

      return resolved !== undefined;
    })
    .map((module) =>
      // https://webpack.js.org/api/module-methods/#magic-comments
      // Tries to load the module if the module function has already been loaded in some other way (e.g. another chunk imported it or a script containing the module was loaded).
      // A Promise is still returned, but only successfully resolves if the chunks are already on the client. If the module is not available, the Promise is rejected. A network request will never be performed.
      import(
        /* webpackMode: "weak" */
        `./stores/${module}`
      )
    );

  return Promise.allSettled(importPromises).then((importResults) => {
    const asyncReducers = importResults
      .filter(({ status }) => status === 'fulfilled')
      .reduce((acc, { value: module }) => {
        const moduleDefault = module.default;
        acc[moduleDefault.name] = moduleDefault.reducer;

        return acc;
      }, {});

    const mountNode = document.getElementById('content');
    const Component = clientComponent(asyncReducers);

    hydrateRoot(mountNode, <Component />);
  });
});

if (!config.isProduction) {
  window.React = React; // enable debugger
}

if (__LEGACY__ === false && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js');
  });
}
